import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbItemSeparator } from "abweb/collection/AbItemSeparator";
import { AbWebServices } from "abweb/service/AbWebServices";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { computed, makeObservable, observable, runInAction } from "mobx";
/**
 * Provides storage and observable access for current user-specific data
 */
export let UserStateProvider = (_dec = observable.shallow, _dec2 = observable.ref, _dec3 = observable.shallow, _dec4 = observable.shallow, _class = class UserStateProvider {
  constructor() {
    _initializerDefineProperty(this, "_myPersonas", _descriptor, this);
    _initializerDefineProperty(this, "_myActivePersona", _descriptor2, this);
    _initializerDefineProperty(this, "_userHasPreviouslyChosenPersona", _descriptor3, this);
    // all of the spaces this user has access to with their current persona
    _initializerDefineProperty(this, "_spaces", _descriptor4, this);
    // all of the spaces included in the current navigation bar, dependent on current persona
    _initializerDefineProperty(this, "_navBarSpaceItems", _descriptor5, this);
    makeObservable(this);
    this._myPersonas = [];
    this._userHasPreviouslyChosenPersona = false;
    this._spaces = {};
    this._navBarSpaceItems = {
      mainItems: [],
      otherItems: []
    };
  }
  get myPersonas() {
    //consumers should be able to alter this array via sorting, so add minimal protection here against shallow alterations
    return [...this._myPersonas];
  }
  set myPersonas(personas) {
    runInAction(() => {
      this._myPersonas = personas;

      // If the active persona is uninitialized or not in the server-returned list
      // of personas, then use the active one from the list.
      if (!this._myActivePersona || !this._myPersonas.find(persona => persona.personaId === this._myActivePersona?.personaId)) {
        this._myActivePersona = this._myPersonas.find(persona => persona.isActive);
      }
    });
  }
  get myActivePersona() {
    return this._myActivePersona;
  }

  // NOTE: this needs to stay private since we require all persona changes go through the logic in setMyActivePersona
  set myActivePersona(activePersona) {
    runInAction(() => {
      this._myActivePersona = activePersona;
    });
  }

  /**
   * Set the active persona and possibly reload the page if this is not the first time choosing a persona
   */
  async setMyActivePersona(personaId) {
    if (!AbWebServices.personaService.personaChangeVetoCallback || (await AbWebServices.personaService.personaChangeVetoCallback(personaId))) {
      const newActivePersona = await AbWebServices.personaService.setMyActivePersona(personaId);
      if (this.userHasPreviouslyChosenPersona) {
        // reload as a hammer to make sure there is no persona-specific data cached
        return BrowserUtils.locationReload();
      }

      // first time selecting a persona
      if (newActivePersona?.personaId !== personaId) {
        // server returned a different active persona than the one requested
        const myPersonaList = await AbWebServices.personaService.getMyPersonas();
        this.myPersonas = myPersonaList.personas;
      }
      this.userHasPreviouslyChosenPersona = true;
      this.myActivePersona = newActivePersona;
    }
  }
  get userHasPreviouslyChosenPersona() {
    return this._userHasPreviouslyChosenPersona;
  }
  set userHasPreviouslyChosenPersona(newValue) {
    runInAction(() => {
      this._userHasPreviouslyChosenPersona = newValue;
    });
  }
  get spaces() {
    return Object.values(this._spaces);
  }
  set spaces(spaces) {
    this._spaces = spaces.reduce((acc, curr) => {
      acc[curr.identifyingName] = curr;
      return acc;
    }, {});
  }
  getSpace(spaceId) {
    return this._spaces[spaceId];
  }

  /**
   * A User's navigation bar is made up of two lists of SpaceInfos and separators,
   * and is dependent on the User's current persona. This data is computed so that
   * the backing SpaceInfo has a single source of record.
   */
  get navBarSpaces() {
    const mainItems = this.navBarSpaceIdentifierListToNavBarSpaceList(this._navBarSpaceItems.mainItems);
    const otherItems = this.navBarSpaceIdentifierListToNavBarSpaceList(this._navBarSpaceItems.otherItems);
    return {
      mainItems,
      otherItems
    };
  }

  /**
   * Lists of Space IDs and separators to show in the navigation bar for this user.
   */
  set navBarSpaceItems(spaceItems) {
    this._navBarSpaceItems = spaceItems;
  }

  /**
   * Convenience method to convert from list of Space IDs and separators to SpaceInfos and separators using
   * the User's current list of Spaces as the record of source
   *
   * @public
   * @param {NavBarSpaceIdentifierList} List of Space IDs and separators to convert to SpaceInfos
   * @returns {NavBarSpaceList}
   */
  navBarSpaceIdentifierListToNavBarSpaceList(list) {
    return list.map(item => {
      if (AbItemSeparator.isSeparator(item)) {
        return item;
      }
      return this.getSpace(item);
    }).filter(item => item !== undefined);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_myPersonas", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_myActivePersona", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_userHasPreviouslyChosenPersona", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_spaces", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "_navBarSpaceItems", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "myPersonas", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "myPersonas"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "myActivePersona", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "myActivePersona"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "userHasPreviouslyChosenPersona", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "userHasPreviouslyChosenPersona"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "spaces", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "spaces"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "navBarSpaces", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "navBarSpaces"), _class.prototype), _class);
//# sourceMappingURL=UserStateProvider.js.map
